import isEmpty from 'lodash/isEmpty';

import type { Action } from 'Lib/reduxType';

import { FILTER_ENTITY_IDS } from './actions';
import {
  ARCHIVED_MATCHES_ENTITY_KEY,
  MATCHES_ENTITY_KEY,
  MESSAGES_ENTITY_KEY,
  OFFERING_ENTITY_KEY,
  PAYWALLS_ENTITY_KEY,
  PROFILE_ENTITY_KEY,
  PURCHASES_ENTITY_KEY,
} from './entityKeys';
import entityMergeStrategies from './entityMergeStrategies';
import type { EntitiesState } from './types';
import { filterEntities, updateEntities } from './utils';

/**
 * Initial entities state
 *
 * Create an empty map for each entity type to declare the entity types that are used.
 */
export const initialState: EntitiesState = {
  [ARCHIVED_MATCHES_ENTITY_KEY]: {},
  [MATCHES_ENTITY_KEY]: {},
  [MESSAGES_ENTITY_KEY]: {},
  [OFFERING_ENTITY_KEY]: {},
  [PAYWALLS_ENTITY_KEY]: {},
  [PROFILE_ENTITY_KEY]: {},
  [PURCHASES_ENTITY_KEY]: {},
};

/**
 * Reducer for entities
 *
 * Entities detected in the payload will be upserted and merged as necessary by default for any
 * actions that aren't special cases.
 *
 * The `FILTER_ENTITY_IDS` action can be used to clean up entities that are no longer needed by
 * supplying a list of entities to keep.
 *
 * @param  {EntitiesState} state - previous state of entities
 * @param  {string} action - action type
 * @return {EntitiesState} updated entities state
 */
export const reducer = (state: EntitiesState = initialState, action: Action) => {
  switch (action.type) {
    case FILTER_ENTITY_IDS: {
      // TODO: Remove 'payload' in action check once actions are typed correctly.
      const idsByType = 'payload' in action ? action.payload?.entityFilterIds || {} : {};
      return filterEntities(state, idsByType, false);
    }

    default: {
      // TODO: Remove 'payload' in action check once actions are typed correctly.
      const updatedEntities = 'payload' in action ? action.payload?.entities : undefined;
      if (isEmpty(updatedEntities)) {
        return state;
      }
      return updateEntities(state, updatedEntities, entityMergeStrategies);
    }
  }
};
